<script lang="ts">
  // (c) Cognisoft Aps, 2022

  import { db } from "../firebase";
  import { collection, addDoc, updateDoc, doc } from "firebase/firestore";
  import { onMount } from "svelte";

  import { Pages, DocType, Level } from "../enums";
  import type { Result, ResultUserEmail, ResultSettings } from "../types";

  import { route } from "../stores/route.svelte";
  import { userEmail, studentUserData } from "../stores/user-store.svelte";
  import { helpCount } from "../stores/helpCount.svelte";
  import { result } from "../stores/result.svelte";

  import { icons, Icons } from "../utilities/icons";
  import { setCompletedBackgroundColor } from "../utilities/setCompletedBackgroundColor";

  import IconButton from "../components/IconButton.svelte";

  import MessageModal from "../modals/MessageModal.svelte";

  // TYPES ---------------------------------------------------------------

  // CONSTANTS ------------------------------------------------------------------

  const maxIndependencyIndexScore = 2;
  const maxIndex = 100;

  // STATE ----------------------------------------------------------------------

  // error messaging
  let showMessageModal = $state(false);
  let errorMessage = $state("");
  let saveResultsError = "Kunne ikke gemme resultater";

  // in order to show correct text even after update of training status variables
  let wasIntro = $state(false);
  let wasBasisTest = $state(false);
  let evalText = $state("");
  let numberOfSpeechTasks: 4 | 8 | 12 = $state(4);

  // variables to determine if modality-icons should be shown
  let goodReading = $state(false);
  let goodComprehension = $state(false);
  let goodSpelling = $state(false);

  // FUNCTIONS ------------------------------------------------------------------

  const startOver = (): void => {
    route.current = Pages.Ready;
  };

  // sum independency-indexes from all items in a modality
  // make 0-100 score by making percentage of obtained score of max score
  const independenceIndexFromItemIndices = (
    ...theArgs: Array<number>
  ): number => {
    const maxScore = theArgs.length * maxIndependencyIndexScore;
    const reducer = (accumulator: number, curr: number): number =>
      accumulator + curr;
    const sum = theArgs.reduce(reducer);
    return (sum / maxScore) * 100;
  };

  // reduce an help count that is from 0 to infinite to a 0-1-2 score;
  // no errors = 2, 1-2 errors = 1, > 2 errors = 0
  const itemIndexFromHelpCount = (count: number): 0 | 1 | 2 => {
    switch (count) {
      case 0:
        return 2;
        break;
      case 1:
      case 2:
        return 1;
        break;
      default:
        return 0;
    }
  };

  const computeIndependencyIndexes = () => {
    const comprehensionHelpArr: Array<number> = [
      itemIndexFromHelpCount(helpCount.value.comprehension.item11),
      itemIndexFromHelpCount(helpCount.value.comprehension.item12),
      itemIndexFromHelpCount(helpCount.value.comprehension.item13),
      itemIndexFromHelpCount(helpCount.value.comprehension.item14),
      itemIndexFromHelpCount(helpCount.value.comprehension.item41),
      itemIndexFromHelpCount(helpCount.value.comprehension.item42),
      itemIndexFromHelpCount(helpCount.value.comprehension.item43),
    ];
    result.value.comprehensionIndependencyIndex =
      independenceIndexFromItemIndices(...comprehensionHelpArr);
    const readingHelpArr: Array<number> = [
      itemIndexFromHelpCount(helpCount.value.reading.item21),
      itemIndexFromHelpCount(helpCount.value.reading.item22),
      itemIndexFromHelpCount(helpCount.value.reading.item23),
      itemIndexFromHelpCount(helpCount.value.reading.item24),
      itemIndexFromHelpCount(helpCount.value.reading.item31),
      itemIndexFromHelpCount(helpCount.value.reading.item32),
      itemIndexFromHelpCount(helpCount.value.reading.item33),
      itemIndexFromHelpCount(helpCount.value.reading.item34),
    ];
    result.value.readingIndependencyIndex = independenceIndexFromItemIndices(
      ...readingHelpArr,
    );
    // 13.02.24: now there may be either 1 or 3 spelling steps
    const spellingHelpArr: Array<number> = studentUserData.value.settings
      .simpleSpelling
      ? [
          itemIndexFromHelpCount(helpCount.value.spelling.item51),
          itemIndexFromHelpCount(helpCount.value.spelling.item52),
          itemIndexFromHelpCount(helpCount.value.spelling.item53),
          itemIndexFromHelpCount(helpCount.value.spelling.item54),
        ]
      : [
          itemIndexFromHelpCount(helpCount.value.spelling.item51),
          itemIndexFromHelpCount(helpCount.value.spelling.item52),
          itemIndexFromHelpCount(helpCount.value.spelling.item53),
          itemIndexFromHelpCount(helpCount.value.spelling.item54),
          itemIndexFromHelpCount(helpCount.value.spelling.item61),
          itemIndexFromHelpCount(helpCount.value.spelling.item62),
          itemIndexFromHelpCount(helpCount.value.spelling.item63),
          itemIndexFromHelpCount(helpCount.value.spelling.item64),
          itemIndexFromHelpCount(helpCount.value.spelling.item71),
          itemIndexFromHelpCount(helpCount.value.spelling.item72),
          itemIndexFromHelpCount(helpCount.value.spelling.item73),
          itemIndexFromHelpCount(helpCount.value.spelling.item74),
        ];
    result.value.spellingIndependencyIndex = independenceIndexFromItemIndices(
      ...spellingHelpArr,
    );
    const namingHelpArr: Array<number> = [
      itemIndexFromHelpCount(helpCount.value.naming.item91),
      itemIndexFromHelpCount(helpCount.value.naming.item92),
      itemIndexFromHelpCount(helpCount.value.naming.item93),
      itemIndexFromHelpCount(helpCount.value.naming.item94),
    ];
    result.value.namingIndependencyIndex = independenceIndexFromItemIndices(
      ...namingHelpArr,
    );
  };

  const saveResults = async () => {
    // // add a new result doc for the user
    // // - but not if stopped before end of the last step 9

    let resultToSave: ResultUserEmail & Result & ResultSettings = {
      email: userEmail.value,
      settings: { ...studentUserData.value.settings },
      session: studentUserData.value.sessionsCompleted,
      startTime: result.value.startTime,
      endTime: result.value.endTime,
      pauseElapsed: result.value.pauseElapsed,
      items: result.value.items,
      // responseDetails: result.value.responseDetails,
      comprehensionCorrectFirstTry: result.value.comprehensionCorrectFirstTry,
      comprehensionIndependencyIndex:
        result.value.comprehensionIndependencyIndex,
      readingCorrectFirstTry: result.value.readingCorrectFirstTry,
      readingIndependencyIndex: result.value.readingIndependencyIndex,
      spellingCorrectFirstTry: result.value.spellingCorrectFirstTry,
      spellingIndependencyIndex: result.value.spellingIndependencyIndex,
      namingCorrectFirstTry: result.value.namingCorrectFirstTry,
      namingIndependencyIndex: result.value.namingIndependencyIndex,
      wasInterrupted: result.value.wasInterrupted,
      lastStepCompleted: result.value.lastStepCompleted,
      currentLevel: studentUserData.value.currentLevel,
    };

    try {
      const docRef = await addDoc(
        collection(db, DocType.Results),
        resultToSave,
      );
    } catch (err) {
      console.error("kunne ikke gemme resultater, fejl: ", err);
    }
  };

  const updateSessionsCompleted = () => {
    if (studentUserData.value.settings.doIntroNextSession) {
      wasIntro = true;
    } else if (studentUserData.value.settings.doTestNextSession) {
      wasBasisTest = true;
    } else {
      studentUserData.value.sessionsCompleted =
        studentUserData.value.sessionsCompleted + 1;
      studentUserData.value.completedOnCurrentLevel =
        studentUserData.value.completedOnCurrentLevel + 1;
    }
  };

  const updateStudentUserDataStore = () => {
    // determine type of run for next time
    if (studentUserData.value.settings.doIntroNextSession) {
      studentUserData.value.settings.doIntroNextSession = false;
      studentUserData.value.settings.doTestNextSession = true;
    } else if (studentUserData.value.settings.doTestNextSession) {
      studentUserData.value.settings.doTestNextSession = false;
      studentUserData.value.settings.doIntroNextSession = false;
    } else {
      if (
        studentUserData.value.completedOnCurrentLevel >=
          studentUserData.value.settings.sessionsBeforeLevelChange &&
        studentUserData.value.currentLevel !== Level.SecondSubject
      ) {
        if (studentUserData.value.currentLevel === Level.First) {
          studentUserData.value.currentLevel = Level.Second;
        } else if (studentUserData.value.currentLevel === Level.Second) {
          studentUserData.value.currentLevel = Level.SecondVerb;
        } else if (studentUserData.value.currentLevel === Level.SecondVerb) {
          studentUserData.value.currentLevel = Level.SecondSubject;
        }
        studentUserData.value.completedOnCurrentLevel = 0;
        studentUserData.value.settings.doTestNextSession = true;
      }
      // no levels beyond SecondSubject
    }
  };

  // update Level, number of sessions etc in user rec
  const updateFirestoreUserDoc = async () => {
    const documentRef = doc(db, DocType.Users, userEmail.value);
    try {
      await updateDoc(documentRef, {
        sessionsCompleted: studentUserData.value.sessionsCompleted,
        currentLevel: studentUserData.value.currentLevel,
        completedOnCurrentLevel: studentUserData.value.completedOnCurrentLevel,
        settings: studentUserData.value.settings,
      });
    } catch (err) {
      console.error("kunne ikke opdatere antal sessioner mv, fejl: ", err);
      errorMessage = saveResultsError;
      showMessageModal = true;
    }
  };

  const makeEvaluationTexts = () => {
    // values to display in temp vars here before update with saving results

    // compute number of speech tasks on level
    if (
      studentUserData.value.settings.doIntroNextSession ||
      studentUserData.value.settings.doTestNextSession
    ) {
      numberOfSpeechTasks = 4;
    } else {
      if (studentUserData.value.currentLevel === Level.SecondVerb) {
        numberOfSpeechTasks = 8;
      } else if (studentUserData.value.currentLevel === Level.SecondSubject) {
        numberOfSpeechTasks = 12;
      } else {
        numberOfSpeechTasks = 4;
      }
    }
    // evaluation test for non-speech tasks
    if (
      // perfect for all 3
      result.value.comprehensionIndependencyIndex === maxIndex &&
      result.value.readingIndependencyIndex === maxIndex &&
      result.value.spellingIndependencyIndex === maxIndex
    ) {
      evalText = "Du var både god til at forstå, læse og stave.";
      goodComprehension = true;
      goodReading = true;
      goodSpelling = true;
    } else if (
      // equally bad for all 3
      result.value.comprehensionIndependencyIndex ===
        result.value.readingIndependencyIndex &&
      result.value.readingIndependencyIndex ===
        result.value.spellingIndependencyIndex &&
      result.value.spellingIndependencyIndex ===
        result.value.comprehensionIndependencyIndex
    ) {
      evalText = "";
    } else if (
      // comprehension best
      result.value.comprehensionIndependencyIndex >
        result.value.readingIndependencyIndex &&
      result.value.comprehensionIndependencyIndex >
        result.value.spellingIndependencyIndex
    ) {
      evalText = "Du var særlig god til at forstå tale.";
      goodComprehension = true;
    } else if (
      // reading best
      result.value.readingIndependencyIndex >
        result.value.comprehensionIndependencyIndex &&
      result.value.readingIndependencyIndex >
        result.value.spellingIndependencyIndex
    ) {
      evalText = "Du var særlig god til at læse.";
      goodReading = true;
    } else if (
      // spelling best
      result.value.spellingIndependencyIndex >
        result.value.readingIndependencyIndex &&
      result.value.spellingIndependencyIndex >
        result.value.comprehensionIndependencyIndex
    ) {
      evalText = "Du var særlig god til at stave.";
      goodSpelling = true;
    } else if (
      // comprehension eq reading, both > spelling
      result.value.comprehensionIndependencyIndex ===
        result.value.readingIndependencyIndex &&
      result.value.comprehensionIndependencyIndex >
        result.value.spellingIndependencyIndex &&
      result.value.readingIndependencyIndex >
        result.value.spellingIndependencyIndex
    ) {
      evalText = "I dag var du særlig god til at forstå og læse";
      goodComprehension = true;
      goodReading = true;
    } else if (
      // comprehension eq spelling, both > reading
      result.value.comprehensionIndependencyIndex ===
        result.value.spellingIndependencyIndex &&
      result.value.comprehensionIndependencyIndex >
        result.value.readingIndependencyIndex &&
      result.value.spellingIndependencyIndex >
        result.value.readingIndependencyIndex
    ) {
      evalText = "I dag var du særlig god til at forstå og stave";
      goodComprehension = true;
      goodSpelling = true;
    } else if (
      // reading eq spelling, both > comprehension
      result.value.spellingIndependencyIndex ===
        result.value.readingIndependencyIndex &&
      result.value.readingIndependencyIndex >
        result.value.comprehensionIndependencyIndex &&
      result.value.spellingIndependencyIndex >
        result.value.comprehensionIndependencyIndex
    ) {
      evalText = "I dag var du særlig god til at læse og stave";
      goodReading = true;
      goodComprehension = true;
    } else {
      console.error("End-results: error in making evalText");
    }
  };

  // INITIALIZE -----------------------------------------------------------

  // do before mount:

  setCompletedBackgroundColor(false);
  result.value.endTime = new Date();
  computeIndependencyIndexes();
  if (!result.value.wasInterrupted) {
    updateSessionsCompleted();
  }
  makeEvaluationTexts();

  onMount(async () => {
    // save results before updating Level etc.
    await saveResults();
    if (!result.value.wasInterrupted) {
      // update user record with new Level etc. in store
      updateStudentUserDataStore();
    }
    // update user record with new Level etc. in firestore database
    await updateFirestoreUserDoc();
  });
</script>

<!-- HTML ===================================================== -->

<main data-page="end-results">
  <div class="results">
    {#if !result.value.wasInterrupted}
      {#if wasIntro}
        Du har nu gennemført et introduktionsforløb. <br />
      {:else if wasBasisTest}
        Basis-test gennemført. <br />
      {:else}
        Godt gået. Du har nu gennemført dit {studentUserData.value
          .sessionsCompleted}. sæt.
        <br />
      {/if}
      {#if evalText !== ""}
        {#if goodComprehension}
          <svg class="svg" viewBox={icons[Icons.EarLight].viewBox}>
            {@html icons[Icons.EarLight].path}
          </svg>
        {/if}
        {#if goodReading}
          <svg class="svg" viewBox={icons[Icons.BookOpenLight].viewBox}>
            {@html icons[Icons.BookOpenLight].path}
          </svg>
        {/if}
        {#if goodSpelling}
          <svg class="svg" viewBox={icons[Icons.PencilLight].viewBox}>
            {@html icons[Icons.PencilLight].path}
          </svg>
        {/if}
        {evalText}
        <br />
      {/if}
      <svg class="svg" viewBox={icons[Icons.LipsLight].viewBox}>
        {@html icons[Icons.LipsLight].path}
      </svg>
      Du var tilfreds med din udtale {result.value.namingCorrectFirstTry} ud af {numberOfSpeechTasks}
      gange.
    {/if}
  </div>
  <div class="result-details">
    {#if result.value.wasInterrupted}
      {#if result.value.lastStepCompleted === 0}
        Træningen blev afbrudt før nogen trin var gennemført.
      {:else if result.value.lastStepCompleted < 9}
        Træningen blev afbrudt. Sidste gennemførte trin: {result.value
          .lastStepCompleted}.
      {/if}
    {/if}
  </div>
  <div class="button-area">
    <IconButton onclick={startOver} textLargeScaled={true} icon={Icons.Repeat}>
      {#snippet message()}Ny træning{/snippet}
    </IconButton>
  </div>
</main>

<!-- MODALs with messages  -->

{#if showMessageModal}
  <MessageModal
    messageModalClosed={() => {
      showMessageModal = false;
    }}
  >
    {#snippet message()}
      {errorMessage}
    {/snippet}
  </MessageModal>
{/if}

<!-- CSS ====================================================== -->
<style>
  main {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-evenly;
    text-align: center;
    height: calc(var(--vh, 1vh) * 100);
  }
  .results {
    font-size: 4vmin;
    line-height: 8vmin;
    padding: 5vw;
  }
  .result-details {
    display: flex;
    flex-direction: column;
    align-items: center;
    font-size: 3vmin;
    line-height: 4vmin;
  }
  .button-area {
    width: 80vw;
    display: flex;
    justify-content: center;
  }
  .svg {
    height: 5vmin;
    width: 5vmin;
    fill: currentColor;
    transform: translate(0, 1vmin);
  }
</style>
